import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Hooks für persistente "Reject"-Aktionen im Wartungsfenster-Drawer
 * (Sprint-3-Carry-Over, WT-205-Followup).
 *
 * Ein "Override" merkt pro Kunde + Feld den abgelehnten Vorschlag inkl.
 * Ablaufdatum. Solange der Override gültig ist und der erneut geparste
 * Vorschlag identisch ist, blendet die UI das Diff-Pair für dieses Feld aus
 * (resp. zeigt es nur als "Override aktiv bis YYYY-MM-DD"-Badge).
 *
 * Backend-Endpunkte (siehe docs/api/openapi.yaml, Erweiterung Sprint 3):
 *   GET    /customers/{id}/parse-overrides
 *   POST   /customers/{id}/parse-overrides         body: { field, rejected_value, days }
 *   DELETE /customers/{id}/parse-overrides/{field}
 */

export type ParseOverrideField =
  | 'frequency'
  | 'weekdays'
  | 'time_start'
  | 'time_end';

export interface ParseOverride {
  field: ParseOverrideField;
  /** Der abgelehnte Vorschlagswert (genau dieser wird unterdrückt). */
  rejected_value: unknown;
  /** ISO-Datum, ab wann der Override wieder erlischt (exklusiv). */
  expires_at: string;
  created_at: string;
}

export interface ParseOverrideListResponse {
  data: ParseOverride[];
}

export type ParseOverrideDuration = 7 | 30 | 90 | -1;

export interface SetParseOverrideInput {
  customerId: string;
  field: ParseOverrideField;
  rejectedValue: unknown;
  /** Anzahl Tage; -1 = "Nie wieder" (Backend setzt expires_at = 9999-12-31). */
  days: ParseOverrideDuration;
}

export interface ResetParseOverrideInput {
  customerId: string;
  field: ParseOverrideField;
}

// --- Query-Keys ------------------------------------------------------------

export const parseOverridesKeys = {
  all: ['parse-overrides'] as const,
  byCustomer: (customerId: string) =>
    ['parse-overrides', 'by-customer', customerId] as const,
};

// --- Queries ---------------------------------------------------------------

export function useParseOverrides(
  customerId: string | undefined
): ReturnType<typeof useQuery<ParseOverrideListResponse>> {
  return useQuery<ParseOverrideListResponse>({
    queryKey: parseOverridesKeys.byCustomer(customerId ?? ''),
    queryFn: () =>
      api.get<ParseOverrideListResponse>(
        `/customers/${customerId ?? ''}/parse-overrides`
      ),
    enabled: Boolean(customerId),
    staleTime: 30_000,
  });
}

// --- Mutations -------------------------------------------------------------

interface SetOverrideBody {
  field: ParseOverrideField;
  rejected_value: unknown;
  days: number;
}

export function useSetParseOverride(): ReturnType<
  typeof useMutation<ParseOverride, Error, SetParseOverrideInput>
> {
  const qc = useQueryClient();
  return useMutation<ParseOverride, Error, SetParseOverrideInput>({
    mutationFn: ({ customerId, field, rejectedValue, days }) => {
      const body: SetOverrideBody = {
        field,
        rejected_value: rejectedValue,
        days,
      };
      return api.post<ParseOverride, SetOverrideBody>(
        `/customers/${customerId}/parse-overrides`,
        body
      );
    },
    onSuccess: (_data, { customerId }) => {
      void qc.invalidateQueries({
        queryKey: parseOverridesKeys.byCustomer(customerId),
      });
    },
  });
}

export function useResetParseOverride(): ReturnType<
  typeof useMutation<void, Error, ResetParseOverrideInput>
> {
  const qc = useQueryClient();
  return useMutation<void, Error, ResetParseOverrideInput>({
    mutationFn: ({ customerId, field }) =>
      api.delete<void>(`/customers/${customerId}/parse-overrides/${field}`),
    onSuccess: (_data, { customerId }) => {
      void qc.invalidateQueries({
        queryKey: parseOverridesKeys.byCustomer(customerId),
      });
    },
  });
}

// --- Helpers ---------------------------------------------------------------

/**
 * Findet einen Override für ein bestimmtes Feld in der Liste.
 * Filtert defensiv abgelaufene Einträge heraus (auf Client-Seite, falls
 * Backend nicht aufräumt).
 */
export function findActiveOverride(
  overrides: ParseOverride[] | undefined,
  field: ParseOverrideField,
  now: Date = new Date()
): ParseOverride | null {
  if (!overrides) return null;
  for (const o of overrides) {
    if (o.field !== field) continue;
    const exp = new Date(o.expires_at);
    if (Number.isNaN(exp.getTime())) continue;
    if (exp.getTime() <= now.getTime()) continue;
    return o;
  }
  return null;
}
